import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getUserFeedFieldPolicy } from './feedFieldPolicy';

/** Where an unconfigured client talks to, i.e. a developer running `yarn server` locally. */
export const LOCAL_GRAPHQL_URL = 'http://localhost:4000/graphql';

/**
 * TRIL-637: keyed on whether an endpoint was CONFIGURED, not on the mode's NAME.
 *
 * The old rule was `MODE !== 'production' → localhost`, which quietly meant that every mode
 * except the two hard-coded ones targeted a machine-local server. `--mode demo` loads
 * `.env.demo`, sets VITE_API_URL correctly, and still sent every query to localhost:4000 —
 * where nothing is listening, so login failed with a bare "failed to fetch". The failure mode is
 * the bad kind: the configuration is present and correct, so nothing points at the mistake.
 * Running a local build against a deployed backend is a genuinely useful smoke setup (it is how
 * the web half of TRIL-620 was verified) and it required knowing this quirk.
 *
 * Now: an explicit endpoint wins in ANY mode, and "no configuration" is what means localhost.
 * `qa`, `staging` and anything else added later work without touching this function.
 *
 * THE PRODUCTION GUARD IS KEPT, and deliberately so — it exists because of a real outage. A
 * production build with neither var set once shipped requests to the literal string
 * `undefined/graphql`. Falling back to localhost here would hide that.
 *
 * BUT THIS THROW IS A RUNTIME ONE, not a build failure — it is compiled INTO the bundle, so
 * `vite build` still exits 0 and still writes dist/, the deploy script's `[ ! -d dist ]` check
 * passes, and the white screen appears in a browser after deploying. The real build-time gate
 * lives in `vite.config.ts` (TRIL-637); this one is the backstop for a bundle built some other
 * way. Saying "loud build failure" here, as an earlier draft of this comment did, described a
 * protection that did not exist.
 */
export function resolveGraphqlUrl(): string {
  const explicit = import.meta.env.VITE_GRAPHQL_URL;
  if (explicit) return explicit;

  // Trailing slash trimmed: `https://api.example.com/` is an ordinary thing to write in a .env
  // and would otherwise produce `https://api.example.com//graphql`.
  const base = (import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');
  if (base) return `${base}/graphql`;

  if (import.meta.env.PROD) {
    throw new Error(
      'Neither VITE_GRAPHQL_URL nor VITE_API_URL is set. A production build cannot determine ' +
        'the GraphQL endpoint. Set one of them in the environment when running vite build.',
    );
  }

  return LOCAL_GRAPHQL_URL;
}

const API_URL = resolveGraphqlUrl();

const TOKEN_KEY = 'accessToken';

const httpLink = createHttpLink({
  uri: API_URL,
  credentials: 'include',
  fetchOptions: {
    mode: 'cors',
  },
});

const authMiddleware = new ApolloLink((operation, forward) => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem(TOKEN_KEY);
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    }));
  }

  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const { message, locations, path } of graphQLErrors) {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      );
    }
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          getDrafts: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          getDraftById: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          getUserFeed: getUserFeedFieldPolicy,
        },
      },
      PostDraft: {
        keyFields: ['id'],
        fields: {
          textBlocks: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          textBlocksVersions: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  link: from([errorLink, authMiddleware, httpLink]),
  // `credentials` belongs on the HttpLink (set above), not the ApolloClient
  // constructor — @apollo/client 3.14 rejects it here (error 103). It was
  // redundant with the httpLink's `credentials: 'include'`.
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
    },
    query: {
      // @ts-ignore
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  },
});
